/**
 * Heat Level System - Police chase escalation
 * 
 * Level 1: 2 patrol cars, casual pursuit
 * Level 2: 4 cars, more aggressive, roadblocks possible
 * Level 3: 6 cars + helicopter spotlight, spike strips
 * Level 4: 8 cars + SWAT SUVs, PIT maneuvers
 * Level 5: 10+ cars, helicopter, total chaos
 */

export type HeatLevel = 1 | 2 | 3 | 4 | 5;

export interface HeatConfig {
  level: HeatLevel;
  copCount: number;
  aggressiveness: number; // 0-1, affects AI behavior
  roadblocksEnabled: boolean;
  spikeStripsEnabled: boolean;
  helicopterEnabled: boolean;
  pitManeuversEnabled: boolean;
  copSpeedBoost: number; // multiplier on cop catch-up
  respawnDelay: number; // seconds before wrecked cops respawn
}

/** 
 * Optimized cop counts for smooth performance:
 * - Level 1-2: Use base cops only (2)
 * - Level 3: +1 extra cop (3 total)
 * - Level 4: +2 extra cops (4 total)  
 * - Level 5: +3 extra cops (5 total)
 */
export const HEAT_CONFIGS: Record<HeatLevel, HeatConfig> = {
  1: {
    level: 1,
    copCount: 2,
    aggressiveness: 0.3,
    roadblocksEnabled: false,
    spikeStripsEnabled: false,
    helicopterEnabled: false,
    pitManeuversEnabled: false,
    copSpeedBoost: 1.0,
    respawnDelay: 10,
  },
  2: {
    level: 2,
    copCount: 2,
    aggressiveness: 0.5,
    roadblocksEnabled: false,
    spikeStripsEnabled: false,
    helicopterEnabled: false,
    pitManeuversEnabled: false,
    copSpeedBoost: 1.1,
    respawnDelay: 8,
  },
  3: {
    level: 3,
    copCount: 3,
    aggressiveness: 0.65,
    roadblocksEnabled: true,
    spikeStripsEnabled: false,
    helicopterEnabled: false,
    pitManeuversEnabled: false,
    copSpeedBoost: 1.2,
    respawnDelay: 6,
  },
  4: {
    level: 4,
    copCount: 4,
    aggressiveness: 0.8,
    roadblocksEnabled: true,
    spikeStripsEnabled: true,
    helicopterEnabled: false,
    pitManeuversEnabled: true,
    copSpeedBoost: 1.35,
    respawnDelay: 4,
  },
  5: {
    level: 5,
    copCount: 5,
    aggressiveness: 1.0,
    roadblocksEnabled: true,
    spikeStripsEnabled: true,
    helicopterEnabled: true,
    pitManeuversEnabled: true,
    copSpeedBoost: 1.5,
    respawnDelay: 3,
  },
};

/** 
 * Heat points required to reach each level.
 * Tuned for ~90 second game:
 * - Level 2 around 20-30 seconds in
 * - Level 3 around 45-50 seconds in  
 * - Level 4 around 65-70 seconds in
 * - Level 5 only if playing very aggressively
 */
const HEAT_THRESHOLDS: Record<HeatLevel, number> = {
  1: 0,
  2: 30,
  3: 70,
  4: 120,
  5: 180,
};

/** Heat gain rates - tuned for 90 second game */
const HEAT_GAIN = {
  /** Per second while being chased (base rate) */
  TIME_IN_CHASE: 0.5,
  /** Per second at high speed (>120 km/h) */
  HIGH_SPEED: 0.3,
  /** Per second at very high speed (>160 km/h) */
  VERY_HIGH_SPEED: 0.5,
  /** One-time when hitting a cop car */
  HIT_COP: 8,
  /** One-time for near miss with cop */
  NEAR_MISS_COP: 3,
  /** Per second while drifting near cops */
  DRIFT_NEAR_COP: 0.8,
  /** One-time when wrecking a cop (takedown) */
  COP_TAKEDOWN: 15,
  /** Per property destroyed */
  PROPERTY_DAMAGE: 2,
};

/** Heat decay rate when not actively engaged */
const HEAT_DECAY_RATE = 0.3; // per second

export interface HeatState {
  points: number;
  level: HeatLevel;
  config: HeatConfig;
  /** Time since last heat gain (for decay) */
  timeSinceGain: number;
  /** Flash effect when level changes */
  levelUpFlash: number;
  /** Previous level for detecting changes */
  prevLevel: HeatLevel;
}

export function createHeatState(): HeatState {
  const startingPoints = 18;
  const startingLevel = calculateLevel(startingPoints);
  return {
    points: startingPoints,
    level: startingLevel,
    config: HEAT_CONFIGS[startingLevel],
    timeSinceGain: 0,
    levelUpFlash: 0,
    prevLevel: startingLevel,
  };
}

function calculateLevel(points: number): HeatLevel {
  if (points >= HEAT_THRESHOLDS[5]) return 5;
  if (points >= HEAT_THRESHOLDS[4]) return 4;
  if (points >= HEAT_THRESHOLDS[3]) return 3;
  if (points >= HEAT_THRESHOLDS[2]) return 2;
  return 1;
}

export function setHeatPoints(state: HeatState, points: number): void {
  state.points = Math.max(0, points);
  state.level = calculateLevel(state.points);
  state.config = HEAT_CONFIGS[state.level];
  state.prevLevel = state.level;
}

export interface HeatUpdateInput {
  dt: number;
  speedKmh: number;
  isDrifting: boolean;
  nearestCopDist: number;
  hitCop: boolean;
  nearMissCop: boolean;
  copTakedown: boolean;
  propertyDestroyed: number;
}

export function updateHeatState(state: HeatState, input: HeatUpdateInput): void {
  const {
    dt,
    speedKmh,
    isDrifting,
    nearestCopDist,
    hitCop,
    nearMissCop,
    copTakedown,
    propertyDestroyed,
  } = input;

  let heatGain = 0;

  // Time-based heat gain
  heatGain += HEAT_GAIN.TIME_IN_CHASE * dt;

  // Speed-based heat
  if (speedKmh > 160) {
    heatGain += HEAT_GAIN.VERY_HIGH_SPEED * dt;
  } else if (speedKmh > 120) {
    heatGain += HEAT_GAIN.HIGH_SPEED * dt;
  }

  // Drifting near cops
  if (isDrifting && nearestCopDist < 30) {
    heatGain += HEAT_GAIN.DRIFT_NEAR_COP * dt;
  }

  // Event-based heat
  if (hitCop) {
    heatGain += HEAT_GAIN.HIT_COP;
  }
  if (nearMissCop) {
    heatGain += HEAT_GAIN.NEAR_MISS_COP;
  }
  if (copTakedown) {
    heatGain += HEAT_GAIN.COP_TAKEDOWN;
  }
  if (propertyDestroyed > 0) {
    heatGain += HEAT_GAIN.PROPERTY_DAMAGE * propertyDestroyed;
  }

  // Apply heat gain
  if (heatGain > 0) {
    state.points += heatGain;
    state.timeSinceGain = 0;
  } else {
    state.timeSinceGain += dt;
    // Decay heat if no action for a while (but never below current level threshold)
    if (state.timeSinceGain > 3) {
      const minPoints = HEAT_THRESHOLDS[state.level];
      state.points = Math.max(minPoints, state.points - HEAT_DECAY_RATE * dt);
    }
  }

  // Cap heat points
  state.points = Math.min(state.points, 1000);

  // Update level
  state.prevLevel = state.level;
  state.level = calculateLevel(state.points);
  state.config = HEAT_CONFIGS[state.level];

  // Level up flash
  if (state.level > state.prevLevel) {
    state.levelUpFlash = 1.0;
  }
  if (state.levelUpFlash > 0) {
    state.levelUpFlash = Math.max(0, state.levelUpFlash - dt * 2);
  }
}

/** Get progress to next heat level (0-1) */
export function getHeatProgress(state: HeatState): number {
  if (state.level === 5) return 1;
  const currentThreshold = HEAT_THRESHOLDS[state.level];
  const nextThreshold = HEAT_THRESHOLDS[(state.level + 1) as HeatLevel];
  return (state.points - currentThreshold) / (nextThreshold - currentThreshold);
}

/** Get spawn positions for cops based on player position */
export function getCopSpawnPositions(
  playerX: number,
  playerZ: number,
  copCount: number,
  mapHalf: number,
): { x: number; z: number; heading: number }[] {
  const positions: { x: number; z: number; heading: number }[] = [];
  const spawnRadius = 150; // Distance from player to spawn cops
  
  for (let i = 0; i < copCount; i++) {
    const angle = (i / copCount) * Math.PI * 2 + Math.random() * 0.5;
    let x = playerX + Math.cos(angle) * spawnRadius;
    let z = playerZ + Math.sin(angle) * spawnRadius;
    
    // Clamp to map bounds
    x = Math.max(-mapHalf + 50, Math.min(mapHalf - 50, x));
    z = Math.max(-mapHalf + 50, Math.min(mapHalf - 50, z));
    
    // Face toward player
    const heading = Math.atan2(playerX - x, playerZ - z);
    
    positions.push({ x, z, heading });
  }
  
  return positions;
}
